<script setup>
// ============================================================================
// BonBoZ: Thống kê > THÔNG BÁO ĐẨY — thông báo có tới được máy của nhân viên
// không, vào giờ nào.
//
// Thứ tự trang theo câu hỏi người đọc (dev) hỏi trước:
//   1. Thanh 24 giờ: mỗi máy, mỗi ngày — giờ nào nhận, giờ nào hụt, đã gửi lại
//      tới máy chưa (`timeline`, PushTimeline.vue)
//   2. Sơ đồ đường đi: tin rơi ở chặng nào (cùng dữ liệu, PushDeliveryPath.vue)
//   3. Bảng thiết bị (`devices`)
//   4. Cần xử lý (`action_items`) và Khách chờ lâu (`waiting`)
// Bộ chọn nhân viên lọc mọi khối trừ "Khách chờ lâu" (tính theo hội thoại).
//
// Thứ KHÔNG đo được: máy có kêu thật không. Hệ điều hành không kể lại chuyện đó
// cho ai. Vì vậy phần "Khách chờ lâu" đo HÀNH ĐỘNG của người — khách đã được
// trả lời chưa — thay cho tiếng chuông.
// ============================================================================
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PushReportAPI from 'dashboard/api/bonboz/pushReport';
import { useAlert } from 'dashboard/composables';
import SectionCard from './SectionCard.vue';
import PushActionItems from './PushActionItems.vue';
import PushDeviceTable from './PushDeviceTable.vue';
import PushAgentFilter from './PushAgentFilter.vue';
import PushTimeline from './PushTimeline.vue';
import PushDeliveryPath from './PushDeliveryPath.vue';
import ReportHeader from '../components/ReportHeader.vue';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';

const { t } = useI18n();

const loading = ref(true);
const report = ref(null);
// Khoảng ngày đang xem, để tải lại sau khi gỡ máy hoặc đổi nhân viên.
const currentRange = ref(null);
// Nhân viên đang xem. null = để server chọn mặc định (người đã bật thông báo);
// lần tải đầu server trả lại danh sách đã chọn, từ đó giữ khi đổi ngày.
const selectedUserIds = ref(null);
// Giữ riêng khỏi `report` để bộ chọn không biến mất khi một lần tải bị lỗi.
const agentFilter = ref({ agents: [], selected_user_ids: [] });
// Loại thiết bị, dùng chung cho thanh 24 giờ và sơ đồ; lọc ở client.
const deviceScope = ref('phone');

const load = async ({ from, to }) => {
  if (!from || !to) return;
  currentRange.value = { from, to };
  loading.value = true;
  try {
    const { data } = await PushReportAPI.show({
      since: from,
      until: to,
      userIds: selectedUserIds.value,
    });
    report.value = data;
    if (data.agent_filter) {
      agentFilter.value = data.agent_filter;
      selectedUserIds.value = data.agent_filter.selected_user_ids;
    }
  } catch {
    report.value = null;
    useAlert(t('BONBOZ.PUSH_REPORT.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const reload = () => currentRange.value && load(currentRange.value);

const changeAgents = userIds => {
  selectedUserIds.value = userIds;
  reload();
};

const timeline = computed(() => report.value?.timeline || null);
const actionItems = computed(() => report.value?.action_items || []);
const devices = computed(() => report.value?.devices || []);

// PR4/PR5: nhắc có thể đi nhóm Telegram của trang; leo thang có thêm
// `pending_config` (chưa có nhóm) và `gave_up` (Telegram hỏng 3 lần).
const waiting = computed(() => {
  const stats = report.value?.waiting || { renotified: 0, telegram: {} };
  const telegram = stats.telegram || {};
  const reminder = stats.reminder || {};
  return {
    renotified: stats.renotified,
    reminderTelegram: reminder['telegram/sent'] || 0,
    telegramSent: telegram.sent || 0,
    telegramMissed:
      (telegram.skipped || 0) +
      (telegram.failed || 0) +
      (telegram.gave_up || 0) +
      (telegram.pending_config || 0),
  };
});
</script>

<template>
  <ReportHeader
    :header-title="$t('BONBOZ.PUSH_REPORT.TITLE')"
    :header-description="$t('BONBOZ.PUSH_REPORT.DESCRIPTION')"
  />

  <div class="flex flex-col w-full gap-4 mx-auto max-w-6xl">
    <div class="flex flex-wrap items-start gap-3">
      <OverviewReportFilters :disabled="loading" @filter-change="load" />
      <PushAgentFilter
        v-if="agentFilter.agents.length"
        :agents="agentFilter.agents"
        :selected="agentFilter.selected_user_ids"
        :loading="loading"
        @apply="changeAgents"
      />
    </div>

    <!-- ============ 1. THANH 24 GIỜ ============ -->
    <PushTimeline
      v-model:device-scope="deviceScope"
      :timeline="timeline"
      :loading="loading"
    />

    <!-- ============ 2. SƠ ĐỒ ĐƯỜNG ĐI ============ -->
    <PushDeliveryPath
      :timeline="timeline"
      :device-scope="deviceScope"
      :loading="loading"
    />

    <!-- ============ 3. THIẾT BỊ ============ -->
    <SectionCard
      :title="$t('BONBOZ.PUSH_REPORT.DEVICES_TITLE')"
      :description="$t('BONBOZ.PUSH_REPORT.DEVICES_DESCRIPTION')"
    >
      <PushDeviceTable :devices="devices" @retired="reload" />
      <p v-if="devices.length" class="m-0 mt-3 text-xs text-n-slate-11">
        {{ $t('BONBOZ.PUSH_REPORT.LIMITS_NOTE') }}
      </p>
    </SectionCard>

    <div class="grid gap-4 lg:grid-cols-2 lg:items-start">
      <!-- ============ 4. CẦN XỬ LÝ ============ -->
      <SectionCard
        :title="$t('BONBOZ.PUSH_REPORT.WARNINGS_TITLE')"
        :description="$t('BONBOZ.PUSH_REPORT.WARNINGS_DESCRIPTION')"
      >
        <p v-if="!actionItems.length" class="m-0 text-sm text-n-teal-11">
          {{ $t('BONBOZ.PUSH_REPORT.NO_WARNINGS') }}
        </p>
        <PushActionItems v-else :items="actionItems" />
      </SectionCard>

      <!-- ============ 5. KHÁCH CHỜ LÂU ============ -->
      <SectionCard
        :title="$t('BONBOZ.PUSH_REPORT.WAITING_TITLE')"
        :description="$t('BONBOZ.PUSH_REPORT.WAITING_DESCRIPTION')"
      >
        <dl class="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 m-0 text-sm">
          <dt class="text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.WAITING_RENOTIFIED') }}
          </dt>
          <dd class="m-0 font-medium tabular-nums text-n-slate-12">
            {{ waiting.renotified }}
          </dd>
          <template v-if="waiting.reminderTelegram">
            <dt class="text-n-slate-11">
              {{ $t('BONBOZ.PUSH_REPORT.WAITING_REMINDER_TELEGRAM') }}
            </dt>
            <dd
              class="m-0 font-medium tabular-nums text-n-slate-12"
              data-testid="push-waiting-reminder-telegram"
            >
              {{ waiting.reminderTelegram }}
            </dd>
          </template>
          <dt class="text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.WAITING_TELEGRAM') }}
          </dt>
          <dd class="m-0 font-medium tabular-nums text-n-slate-12">
            {{ waiting.telegramSent }}
          </dd>
          <template v-if="waiting.telegramMissed">
            <dt class="text-n-ruby-11">
              {{ $t('BONBOZ.PUSH_REPORT.WAITING_TELEGRAM_MISSED') }}
            </dt>
            <dd class="m-0 font-medium tabular-nums text-n-ruby-11">
              {{ waiting.telegramMissed }}
            </dd>
          </template>
        </dl>
        <p class="m-0 mt-3 text-xs text-n-slate-11">
          {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.WAITING_NOTE') }}
        </p>
      </SectionCard>
    </div>
  </div>
</template>
